import { Outlet, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, CAN_ASSIGN_ROLES, canViewHotLeads, canViewAI, isSuperAdmin } from "../lib/states";
import SystemAlerts from "./SystemAlerts";
import {
  LayoutGrid, BarChart3, Users2, Settings as SettingsIcon,
  Search, LogOut, Shuffle, Flame, UserCheck, Menu, X, CalendarCheck, Wallet, Sparkles, Star, Activity,
} from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin" || role === "super_admin";
  const [newHot, setNewHot] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const poll = () => api.get("/notifications").then((r) => setNewHot(r.data.new_hot || 0)).catch(() => {});
    poll();
    const id = setInterval(poll, 15000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  const nav = [
    { to: "/", label: "Lead Pipeline", icon: LayoutGrid, testid: "nav-pipeline", show: true },
    { to: "/today", label: "Today's Actions", icon: CalendarCheck, testid: "nav-today", show: true },
    { to: "/order-pipeline", label: "Order Pipeline", icon: Wallet, testid: "nav-order-pipeline", show: true },
    { to: "/hot", label: "Hot Leads", icon: Flame, testid: "nav-hot", show: canViewHotLeads(user), badge: newHot },
    { to: "/ai-stars", label: "AI Stars", icon: Star, testid: "nav-ai-stars", show: canViewAI(user) },
    { to: "/ai-insights", label: "AI Insights", icon: Sparkles, testid: "nav-ai-insights", show: canViewAI(user) },
    { to: "/activity-monitor", label: "Activity Monitor", icon: Activity, testid: "nav-activity-monitor", show: isSuperAdmin(user) },
    { to: "/research", label: "Research", icon: Search, testid: "nav-research", show: isAdmin },
    { to: "/performance", label: "By Executive", icon: UserCheck, testid: "nav-performance", show: isAdmin },
    { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", show: isAdmin },
    { to: "/assign", label: "Bulk Assign", icon: Shuffle, testid: "nav-assign", show: CAN_ASSIGN_ROLES.includes(role) },
    { to: "/users", label: "Users", icon: Users2, testid: "nav-users", show: isAdmin },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", show: isAdmin },
  ];

  const SidebarBody = ({ onNavigate }) => (
    <>
      <div className="px-5 h-16 flex items-center gap-2.5 border-b border-stone-200 shrink-0">
        <div className="h-10 w-10 rounded-lg bg-stone-100 flex items-center justify-center p-1">
          <img src="/unwind-mark.png" alt="UNWIND" className="w-full h-full object-contain" />
        </div>
        <div className="leading-tight">
          <p className="font-heading text-base tracking-[0.15em] text-stone-900">UNWIND</p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Leads CRM</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {nav.filter((n) => n.show).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-200/60 hover:text-stone-900"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            <span className="flex-1">{n.label}</span>
            {n.badge > 0 && (
              <span data-testid="hot-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">
                {n.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-stone-200 shrink-0">
        {isAdmin && <SystemAlerts />}
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-semibold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="leading-tight min-w-0">
            <p className="text-sm font-medium text-stone-900 truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-[11px] text-stone-500 truncate">{ROLE_LABELS[role]}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-stone-600 hover:bg-stone-200/60 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAFAF9]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-stone-200 bg-[#F5F5F4] flex-col">
        <SidebarBody />
      </aside>

      {/* Mobile slide-in drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-stone-900/40 animate-fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 max-w-[80%] bg-[#F5F5F4] flex flex-col h-full shadow-2xl animate-slide-in">
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close"
              className="absolute top-4 right-3 p-1 rounded hover:bg-stone-200 text-stone-500 z-10">
              <X className="h-5 w-5" />
            </button>
            <SidebarBody onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden h-14 shrink-0 flex items-center gap-3 px-4 border-b border-stone-200 bg-[#F5F5F4]">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-nav-toggle"
            className="p-1.5 -ml-1 rounded-md text-stone-700 hover:bg-stone-200/70">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-stone-100 flex items-center justify-center p-0.5">
              <img src="/unwind-mark.png" alt="UNWIND" className="w-full h-full object-contain" />
            </div>
            <span className="font-heading text-base tracking-[0.15em] text-stone-900">UNWIND</span>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col min-w-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
